import React, { useState, type ReactElement } from 'react';
import { uniqBy, sortBy } from 'lodash';
import { Skeleton, Table } from 'antd';
import { Link } from 'react-router-dom';

import NewSspButton from './NewSspButton';
import { Search } from './Search';
import { useSsps, type Ssp } from '../../../hooks/useSsps';
import { PageLayout } from '../../../components/PageLayout';
import { useDisabledModules } from '../../../hooks/useDisabledModules';

const getQps = (lastHourStatistics?: { sspBidRequest: number }): number =>
  lastHourStatistics ? Math.ceil(lastHourStatistics.sspBidRequest / 3600) : 0;

export function SspsPage(): ReactElement {
  const { ssps, isBaseLoaded, isLastHourStatsLoaded, isSpendLoaded } = useSsps();
  const hasSwapper = !useDisabledModules().includes('swapper');
  const [searchQuery, setSearchQuery] = useState<string>();

  const sspsToDisplay = searchQuery
    ? ssps.filter(({ id, name }) => `[${id}] ${name}`.toLowerCase().includes(searchQuery.toLowerCase()))
    : ssps;
  const companies = (ssps || [])
    .map((ssp) => ssp.company)
    .filter((company): company is NonNullable<(typeof ssps)[number]['company']> => Boolean(company));

  return (
    <PageLayout>
      <NewSspButton />

      <Search onSearch={setSearchQuery} />

      <Table
        columns={[
          {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (isActive ? 'Active' : 'Inactive'),
            filters: [
              {
                text: 'Active',
                value: 1,
              },
              {
                text: 'Inactive',
                value: 0,
              },
            ],
            onFilter: (value, { isActive }) => isActive === value,
            width: 100,
          },
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (name, { id }) => (
              <Link to={`/ssps/${id}`}>
                [{id}] {name}
              </Link>
            ),
            width: 100,
          },
          {
            title: 'Company',
            dataIndex: 'company.firstName',
            key: 'company.firstName',
            render: (value, record) => record.company?.firstName || '-',
            filters: uniqBy(sortBy(companies, 'firstName'), 'id').map((company) => ({
              text: company.firstName || '',
              value: company.id,
            })),
            onFilter: (value, { company }) => company?.id === value,
            width: 100,
          },
          {
            title: 'Margin %',
            dataIndex: 'margin',
            key: 'margin',
            render: (value) => value || 0,
            // @ts-expect-error TODO
            sorter: (a, b) => a.margin - b.margin,
            width: 100,
          },
          {
            title: 'Rev. Share %',
            dataIndex: 'revenueSharePercent',
            key: 'revenueSharePercent',
            render: (value) => value || 0,
            // @ts-expect-error TODO
            sorter: (a, b) => a.revenueSharePercent - b.revenueSharePercent,
            width: 100,
          },
          ...(hasSwapper
            ? [
                {
                  title: 'Swapper',
                  dataIndex: 'swapperEnableSwapper',
                  key: 'swapperEnableSwapper',
                  render: (isActive: boolean | null) => (isActive ? 'Active' : 'Inactive'),
                  filters: [
                    {
                      text: 'Active',
                      value: true,
                    },
                    {
                      text: 'Inactive',
                      value: false,
                    },
                  ],
                  onFilter: (value: unknown, { swapperEnableSwapper }: Ssp) => swapperEnableSwapper === value,
                  width: 100,
                },
              ]
            : []),
          {
            title: 'Optimizer',
            dataIndex: 'optimizer',
            key: 'optimizer',
            render: (optimizer) => (optimizer ? 'Active' : 'Inactive'),
            filters: [
              {
                text: 'Active',
                value: 1,
              },
              {
                text: 'Inactive',
                value: 0,
              },
            ],
            onFilter: (value, { optimizer }) => optimizer === value,
            width: 100,
          },
          {
            title: 'QPS',
            dataIndex: 'qps',
            key: 'qps',
            // @ts-expect-error TODOL fix this
            render: (value, { lastHourStatistics, limitQps }) =>
              isLastHourStatsLoaded ? (
                `${getQps(lastHourStatistics) || 0} / ${limitQps || 0}`
              ) : (
                <Skeleton.Input active size="small" style={{ width: 70 }} />
              ),
            defaultSortOrder: 'descend',
            sorter: (a, b) => getQps(a.lastHourStatistics) - getQps(b.lastHourStatistics),
            width: 100,
          },
          {
            title: 'Spend Today',
            dataIndex: 'dailySpend',
            key: 'dailySpend',
            render: (value, record) =>
              isSpendLoaded ? `${value || 0} / ${record.dailySpendLimit || 0}` : <Skeleton.Input active size="small" />,
            sorter: (a, b) => (a.dailySpend || 0) - (b.dailySpend || 0),
            width: 100,
          },
          {
            title: 'Spend Total',
            dataIndex: 'totalSpend',
            key: 'totalSpend',
            render: (value, record) =>
              isSpendLoaded ? `${value || 0} / ${record.totalSpendLimit || 0}` : <Skeleton.Input active size="small" />,
            sorter: (a, b) => (a.totalSpend || 0) - (b.totalSpend || 0),
            width: 100,
          },
        ]}
        dataSource={sspsToDisplay}
        pagination={false}
        size="small"
        loading={!isBaseLoaded}
        scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
      />
    </PageLayout>
  );
}
